import React, { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "services/api";

interface PlatformUser {
 id: string;
 email: string;
 name: string;
 role: "super_admin" | "support";
}

interface PlatformAuthContextType {
 user: PlatformUser | null;
 loading: boolean;
 login: (email: string, password: string) => Promise<void>;
 logout: () => void;
 isAuthenticated: boolean;
}

export const PlatformAuthContext = createContext<PlatformAuthContextType | undefined>(undefined);

export const usePlatformAuth = () => {
 const context = useContext(PlatformAuthContext);
 if (!context) {
  throw new Error("usePlatformAuth must be used within a PlatformAuthProvider");
 }
 return context;
};

interface PlatformAuthProviderProps {
 children: ReactNode;
}

export const PlatformAuthProvider: React.FC<PlatformAuthProviderProps> = ({ children }) => {
 const [user, setUser] = useState<PlatformUser | null>(null);
 const [loading, setLoading] = useState(true);
 const navigate = useNavigate();

 // Check for existing platform auth token on mount
 useEffect(() => {
  const platformToken = localStorage.getItem("platformToken");
  if (platformToken) {
   fetchPlatformUser();
  } else {
   setLoading(false);
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
 }, []);

 const fetchPlatformUser = async () => {
  try {
   const platformToken = localStorage.getItem("platformToken");
   if (!platformToken) {
    setLoading(false);
    return;
   }

   const response = await api.get("/platform/auth/me", {
    headers: {
     Authorization: `Bearer ${platformToken}`,
    },
   });

   setUser(response.data.user);
  } catch (error) {
   console.error("Error fetching platform user:", error);
   localStorage.removeItem("platformToken");
   setUser(null);
  } finally {
   setLoading(false);
  }
 };

 const login = async (email: string, password: string) => {
  try {
   const response = await api.post("/platform/auth/login", {
    email,
    password,
   });

   const { token, user: userData } = response.data;
   localStorage.setItem("platformToken", token);
   setUser(userData);
  } catch (error: any) {
   throw new Error(
    error.response?.data?.error || "Login failed"
   );
  }
 };

 const logout = () => {
  localStorage.removeItem("platformToken");
  setUser(null);
  navigate("/platform/login");
 };

 const isAuthenticated = !!user;

 return (
  <PlatformAuthContext.Provider
   value={{ user, loading, login, logout, isAuthenticated }}
  >
   {children}
  </PlatformAuthContext.Provider>
 );
};

